import type { WorldCapabilities } from "@uzverse/contracts";
import { SNAP_GRID_M, checkPlacement, degToRad, pointInBounds, snap } from "@uzverse/world-scene";
import type {
  ParcelBounds,
  PlacementResult,
  PlacementWorld,
  SceneNode,
} from "@uzverse/world-scene";
import { footprintsOf } from "../footprints.js";
import { rampCandidate } from "../look/garage.js";
import type { Footprint } from "../footprints.js";

/**
 * Blueprint editing rules that run in the browser for instant feedback. They mirror the server:
 * `checkPlacement` is the same pure function `WorldView` uses, and `canArrange` mirrors which
 * buildings `GET /api/v1/world/capabilities` lets the user move. The server still decides.
 */

export interface SceneParcel {
  id: string;
  ownerCompanyId: string | null;
  bounds: ParcelBounds;
  purpose: string | null;
}

/** Parcels in the scene (`world.parcel` nodes with bounds). */
export function parcelsOf(nodes: Iterable<SceneNode>): SceneParcel[] {
  const out: SceneParcel[] = [];
  for (const n of nodes) {
    if (n.kind !== "parcel" || n.geometry.kind !== "procedural") continue;
    const b = n.geometry.params["bounds"] as Partial<ParcelBounds> | undefined;
    if (
      !b ||
      typeof b.minX !== "number" ||
      typeof b.minZ !== "number" ||
      typeof b.maxX !== "number" ||
      typeof b.maxZ !== "number"
    ) {
      continue;
    }
    const purpose = n.geometry.params["purpose"];
    out.push({
      id: n.id,
      ownerCompanyId: n.ownerCompanyId,
      bounds: { minX: b.minX, minZ: b.minZ, maxX: b.maxX, maxZ: b.maxZ },
      purpose: typeof purpose === "string" ? purpose : null,
    });
  }
  return out;
}

export interface Arrangeable {
  /** The parcel the building is confined to (null: a map editor may place it anywhere free). */
  parcelId: string | null;
}

/**
 * May the user drag this building? Only company placements (never the landmark or objects):
 * a map editor any of them; otherwise the ones standing inside a parcel the user may arrange.
 */
export function canArrange(
  fp: Footprint,
  caps: Pick<WorldCapabilities, "canEditMap" | "arrangeableParcelIds" | "parcels"> | null,
  parcels: readonly SceneParcel[],
): Arrangeable | null {
  if (!caps || fp.node.entityType !== "world.placement") return null;
  if (caps.canEditMap) return { parcelId: null };
  const allowed = new Set(caps.arrangeableParcelIds);
  const inside = parcels.find((p) => allowed.has(p.id) && pointInBounds(fp.x, fp.z, p.bounds));
  return inside ? { parcelId: inside.id } : null;
}

/** Position on the 1 m grid (or the user's grid setting). */
export function snapPoint(x: number, z: number, grid: number = SNAP_GRID_M): [number, number] {
  // `+ 0` folds -0 into 0, so a snapped coordinate never serialises as "-0".
  return [snap(x, grid) + 0, snap(z, grid) + 0];
}

/** Yaw in degrees normalised to [0, 360). */
export function normalizeYawDeg(deg: number): number {
  const r = ((deg % 360) + 360) % 360;
  return Math.round(r * 1e6) / 1e6 + 0;
}

/** One 90 degree turn (the `R` key); a quarter turn keeps footprints on the grid. */
export function rotateQuarter(yawDeg: number, direction: 1 | -1 = 1): number {
  return normalizeYawDeg(yawDeg + 90 * direction);
}

export interface Pose {
  x: number;
  z: number;
  yawDeg: number;
}

/** The world a candidate is checked against: parcels and every other building's footprint. */
export function placementWorld(
  nodes: Iterable<SceneNode>,
  parcels: readonly SceneParcel[],
): PlacementWorld {
  const list = [...nodes];
  return {
    parcels: parcels.map((p) => ({ id: p.id, bounds: p.bounds })),
    footprints: footprintsOf(list)
      // The landmark is a world object but it occupies ground like a building.
      .filter((f) => f.kind === "building" || f.node.entityType === "world.object")
      .flatMap((f) => {
        const body = {
          id: f.id,
          kind: "building" as const,
          x: f.x,
          z: f.z,
          yawRad: f.yaw,
          width: f.width,
          depth: f.depth,
        };
        // A garage ramp occupies the ground beside its building: nothing else may stand on it.
        const params = f.node.geometry.kind === "procedural" ? f.node.geometry.params : {};
        const ramp = rampCandidate(params, { x: f.x, z: f.z, yawRad: f.yaw });
        return ramp ? [body, { ...body, ...ramp, id: rampId(f.id) }] : [body];
      }),
  };
}

/** The id of a building's garage ramp in the placement world. */
export const rampId = (buildingId: string): string => `${buildingId}~ramp`;

export interface Preview {
  pose: Pose;
  result: PlacementResult;
}

/** The ghost of a drag: the pointer position snapped to the grid and checked like the server will. */
export function previewPlacement(
  fp: Footprint,
  raw: { x: number; z: number; yawDeg: number },
  rule: Arrangeable,
  world: PlacementWorld,
  grid: number = SNAP_GRID_M,
): Preview {
  const [x, z] = snapPoint(raw.x, raw.z, grid);
  const yawDeg = normalizeYawDeg(raw.yawDeg);
  const yawRad = degToRad(yawDeg);
  // The building's own ramp moves with it: it must fit too, and is not an obstacle to itself.
  const others: PlacementWorld = {
    ...world,
    footprints: world.footprints.filter((f) => f.id !== rampId(fp.id)),
  };
  const result = checkPlacement(
    {
      kind: "building",
      selfId: fp.id,
      x,
      z,
      yawRad,
      width: fp.width,
      depth: fp.depth,
      parcelId: rule.parcelId,
    },
    others,
  );
  if (result.ok && fp.node.geometry.kind === "procedural") {
    const ramp = rampCandidate(fp.node.geometry.params, { x, z, yawRad });
    if (ramp) {
      const rampResult = checkPlacement(
        { kind: "building", selfId: fp.id, ...ramp, parcelId: rule.parcelId },
        others,
      );
      if (!rampResult.ok) return { pose: { x, z, yawDeg }, result: rampResult };
    }
  }
  return { pose: { x, z, yawDeg }, result };
}

/** Which arrangeable building lies under the ground point (topmost = last drawn wins)? */
export function hitFootprint(
  footprints: readonly Footprint[],
  x: number,
  z: number,
  accept: (fp: Footprint) => boolean,
): Footprint | null {
  for (let i = footprints.length - 1; i >= 0; i--) {
    const f = footprints[i]!;
    if (!accept(f)) continue;
    const dx = x - f.x;
    const dz = z - f.z;
    const c = Math.cos(f.yaw);
    const s = Math.sin(f.yaw);
    const lx = dx * c - dz * s;
    const lz = dx * s + dz * c;
    if (Math.abs(lx) <= f.width / 2 && Math.abs(lz) <= f.depth / 2) return f;
  }
  return null;
}
